import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTheme } from '../lib/theme';
import {
  getReceipt, patchReceipt, deleteReceipt, getCategories, listImages, imageUrl, listReceipts,
  patchReceiptTaxCode, getTaxCodes,
} from '../lib/api';
import ReceiptForm from '../components/receipt-detail/ReceiptForm';
import ReceiptImages from '../components/receipt-detail/ReceiptImages';
import ReceiptActions from '../components/receipt-detail/ReceiptActions';
import AssetBanner from '../components/receipt-detail/AssetBanner';
import CategorySuggestionChip from '../components/receipt-detail/CategorySuggestionChip';
import RuleSuggestionModal from '../components/receipt-detail/RuleSuggestionModal';
import SplitEditorModal from '../components/receipt-detail/SplitEditorModal';
import PublishToQbButton from '../components/receipt-detail/PublishToQbButton';
import IsPersonalToggle from '../components/receipt-detail/IsPersonalToggle';
import PaymentMethodPicker from '../components/receipt-detail/PaymentMethodPicker';
import TaxCodePicker, { TaxCodePills } from '../components/TaxCodePicker';
import { clearSplitsApi } from '../lib/api';

// ---------------- Tax Check Panel (Phase 16 — Tax Rule Engine) ----------------
function TaxCheckPanel({ analysis, taxCodeOverride, colors, onChangeTaxCode }) {
  if (!analysis) return null;
  const { expected, actual_total_tax, discrepancy, discrepancy_pct, flag, tolerance_pct } = analysis;
  const isMatch = flag === 'match';
  const isZero = expected.treatment === 'exempt' || expected.treatment === 'zero_rated';
  const accent = isMatch ? colors.accent : colors.warning;
  const bg = isMatch
    ? 'linear-gradient(135deg, rgba(15,200,98,0.10) 0%, rgba(0,229,160,0.05) 100%)'
    : 'linear-gradient(135deg, rgba(217,119,6,0.14) 0%, rgba(245,158,11,0.06) 100%)';

  const expectedLabel = expected.components.length
    ? expected.components.map((c) => `${c.name} ${(c.rate * 100).toFixed(c.rate === 0.09975 ? 3 : 0)}%`).join(' + ')
    : (isZero ? (expected.treatment === 'zero_rated' ? 'Zero-rated (0% tax)' : 'Exempt (no tax)') : 'No profile');

  const treatmentPill = ({
    standard: 'Standard',
    zero_rated: 'Zero-rated',
    exempt: 'Exempt',
    provincial_exempt: 'Provincial exempt',
  })[expected.treatment] || expected.treatment;

  const reason = (() => {
    if (isMatch) return isZero ? 'No tax expected — none charged.' : 'Tax looks correct.';
    if (isZero) return 'Tax should be $0 (zero-rated/exempt) — receipt shows tax charged. Verify category or receipt.';
    return discrepancy > 0
      ? 'Tax charged higher than expected — verify receipt or category.'
      : 'Tax charged lower than expected — verify receipt or category.';
  })();

  return (
    <div
      data-testid="tax-check-panel"
      className="rounded-2xl p-6 mb-4 relative overflow-hidden"
      style={{
        background: bg,
        border: `1px solid ${accent}55`,
        borderLeftWidth: 3,
        boxShadow: `0 4px 24px -14px ${accent}66, inset 0 1px 0 rgba(255,255,255,0.06)`,
      }}>
      <div className="flex items-start justify-between mb-3 relative z-10">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: accent }}>
            Tax check · {expected.province}
          </p>
          <p className="text-base font-bold mt-1" style={{ color: colors.text, letterSpacing: '-0.01em' }}>
            {isMatch ? 'Tax looks correct' : 'Tax mismatch detected'}
          </p>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
          style={{ backgroundColor: `${colors.accent}22`, color: colors.accent, border: `1px solid ${colors.accent}44` }}>
          {treatmentPill}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4 relative z-10 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Expected</p>
          <p className="text-sm font-semibold tabular-nums" style={{ color: colors.text }}>
            ${expected.expected_total_tax.toFixed(2)}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>{expectedLabel}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Actual</p>
          <p className="text-sm font-semibold tabular-nums" style={{ color: colors.text }}>
            ${Number(actual_total_tax || 0).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Discrepancy</p>
          <p className="text-sm font-semibold tabular-nums" style={{ color: isMatch ? colors.text : accent }}>
            {discrepancy >= 0 ? '+' : ''}${Number(discrepancy).toFixed(2)}
            {expected.expected_total_tax > 0 ? ` (${(discrepancy_pct * 100).toFixed(1)}%)` : ''}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>
            Tolerance ±{(tolerance_pct * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 relative z-10">
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 22, height: 22, backgroundColor: `${accent}22`, color: accent,
            fontSize: 13, fontWeight: 800,
          }}>
          {isMatch ? '✓' : '!'}
        </span>
        <p className="text-[13px]" style={{ color: colors.text }}>{reason}</p>
      </div>

      {/* Phase 20 — user tax-code override row */}
      <div
        className="flex items-center gap-2 relative z-10 mt-3 pt-3"
        style={{ borderTop: `1px solid ${colors.border}66` }}
        data-testid="tax-code-override-row"
      >
        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>
          Tax code
        </p>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {taxCodeOverride && (taxCodeOverride.no_tax_applicable
            || taxCodeOverride.federal_code || taxCodeOverride.provincial_code) ? (
            <TaxCodePills value={taxCodeOverride} catalog={analysis._catalog} colors={colors} />
          ) : (
            <span className="text-[12px]" style={{ color: colors.textMuted }}>
              Auto-detected
            </span>
          )}
        </div>
        <button
          data-testid="tax-code-change-btn"
          onClick={onChangeTaxCode}
          className="text-[12px] font-semibold rounded-md px-2.5 py-1"
          style={{ color: colors.accent, backgroundColor: `${colors.accent}18`, border: `1px solid ${colors.accent}44` }}
        >
          Change
        </button>
      </div>
    </div>
  );
}


export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colors } = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [images, setImages] = useState([]);
  const [nextPendingId, setNextPendingId] = useState(null);
  const [pendingLeft, setPendingLeft] = useState(0);
  const [ruleSuggestion, setRuleSuggestion] = useState(null);
  const [splitEditorOpen, setSplitEditorOpen] = useState(false);

  // Edit modal
  const [editField, setEditField] = useState(null); // { key, label, kind }
  const [editValue, setEditValue] = useState('');

  // Phase 20 — tax-code override picker
  const [taxCodePickerOpen, setTaxCodePickerOpen] = useState(false);
  const [taxCatalog, setTaxCatalog] = useState(null);
  useEffect(() => {
    getTaxCodes().then(setTaxCatalog).catch(() => setTaxCatalog(null));
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1500);
  }, []);

  const loadNextPending = useCallback(async () => {
    try {
      const lst = await listReceipts({ status: 'pending', sort: 'confidence_asc', limit: 2 });
      const next = lst.items.find((r) => r.id !== id) ?? null;
      setNextPendingId(next ? next.id : null);
      setPendingLeft(Math.max(0, lst.total - (lst.items.some((r) => r.id === id) ? 1 : 0)));
    } catch (err) {
      console.error('Failed to compute next-pending navigation:', err);
      setNextPendingId(null);
      setPendingLeft(0);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const [r, cats] = await Promise.all([getReceipt(id), getCategories()]);
        setData(r);
        setNotes(r.notes ?? '');
        setCategories(cats.categories);
        try {
          const imgs = await listImages(id);
          // Phase 26C+ — use server-provided URLs directly. When Tigris is
          // enabled these are presigned (self-authenticating). When not, they
          // are backend proxy paths that would need a Bearer header the
          // browser's <img> can't send; fall back to the imageUrl helper
          // then. We detect "already-absolute signed URL" by scheme.
          const urls = (imgs.urls || []).map((u, idx) =>
            /^https?:\/\//i.test(u) ? u : imageUrl(id, idx)
          );
          setImages(urls);
        } catch (err) {
          // Images are optional (manual/email w/o attachment); log only
          console.error('Failed to list receipt images:', err);
        }
        loadNextPending();
      } catch (err) {
        console.error('Failed to load receipt detail:', err);
      }
      setLoading(false);
    })();
  }, [id, loadNextPending]);

  const applyPatch = useCallback(async (patch) => {
    setSaving(true);
    try {
      const updated = await patchReceipt(id, patch);
      // Backend may return `suggest_rule` for follow-up prompting.
      const { suggest_rule: sr, ...rest } = updated || {};
      setData(rest);
      if (sr) setRuleSuggestion(sr);
      showToast('Saved');
    } catch (e) {
      showToast(`Save failed: ${e?.detail ?? 'unknown'}`);
    }
    setSaving(false);
  }, [id, showToast]);

  const onSaveEdit = useCallback(() => {
    if (!editField || !data) return;
    const trimmed = editValue.trim();
    let patch = {};
    if (editField.kind === 'decimal') {
      const parsed = parseFloat(trimmed);
      patch[editField.key] = { value: Number.isFinite(parsed) ? parsed : null, confidence: 1.0 };
    } else if (editField.kind === 'date') {
      const ok = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
      patch.date = { value: ok ? trimmed : null, confidence: ok ? 1.0 : 0.0 };
    } else {
      patch.vendor = { value: trimmed || null, confidence: 1.0 };
    }
    setEditField(null);
    applyPatch(patch);
  }, [editField, editValue, data, applyPatch]);

  const onSaveNotes = useCallback(() => {
    if (!notesDirty) return;
    applyPatch({ notes: notes || null }).then(() => setNotesDirty(false));
  }, [notes, notesDirty, applyPatch]);

  const advanceOrHome = useCallback(() => {
    if (nextPendingId) {
      navigate(`/receipts/${nextPendingId}`, { replace: true });
    } else {
      showToast('All caught up!');
      setTimeout(() => navigate('/receipts', { replace: true }), 700);
    }
  }, [nextPendingId, navigate, showToast]);

  const onStatus = useCallback(async (status) => {
    await applyPatch({ status });
    advanceOrHome();
  }, [applyPatch, advanceOrHome]);

  const onDelete = useCallback(async () => {
    if (!window.confirm('Delete this receipt? This cannot be undone.')) return;
    await deleteReceipt(id);
    navigate('/receipts');
  }, [id, navigate]);

  const openEdit = useCallback((field) => {
    if (!data) return;
    const f = data[field.key];
    setEditField(field);
    setEditValue(f?.value != null ? String(f.value) : '');
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-20">
        <div
          className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: colors.border, borderTopColor: colors.accent }}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 gap-4" data-testid="receipt-not-found">
        <p className="text-xl font-bold" style={{ color: colors.text }}>Receipt not found</p>
        <button
          onClick={() => navigate('/receipts')}
          className="px-6 py-2.5 rounded-full text-sm font-bold text-white"
          style={{ backgroundColor: colors.accent }}
        >
          Back to receipts
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32" data-testid="receipt-detail-page">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="detail-back-btn"
          onClick={() => navigate('/receipts')}
          className="flex items-center gap-1 text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: colors.textMuted }}
        >
          <ChevronLeft size={18} />
          Back
        </button>
        <span
          className="text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: colors.textMuted }}
        >
          Receipt · {data.status}
        </span>
        {nextPendingId ? (
          <button
            data-testid="detail-next-btn"
            onClick={advanceOrHome}
            className="flex items-center gap-1 text-sm font-medium transition-opacity hover:opacity-70 relative"
            style={{ color: colors.textMuted }}
          >
            Next
            <ChevronRight size={18} />
            {pendingLeft > 0 && (
              <span
                data-testid="detail-left-badge"
                className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
                style={{ backgroundColor: colors.accent }}
              >
                {pendingLeft}
              </span>
            )}
          </button>
        ) : (
          <div className="w-10" />
        )}
      </div>

      <AssetBanner
        data={data}
        colors={colors}
        onMove={() => {
          // Scroll to category picker; nothing else required — user picks from Assets group
          const el = document.querySelector('[data-testid="category-picker"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
        onConfirm={async () => {
          try {
            const { confirmAssetSuggestion } = await import('../lib/api');
            await confirmAssetSuggestion(id);
            const u = await getReceipt(id);
            setData(u);
          } catch (e) {
            try { window.dispatchEvent(new CustomEvent('clasifi:toast', { detail: 'Failed to confirm as asset' })); } catch { /* noop */ }
          }
        }}
        onReassign={() => {
          const el = document.querySelector('[data-testid="category-picker"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
        onDismiss={async () => {
          const sug = data?.asset_suggestion;
          if (sug && sug.status === 'pending') {
            try {
              const { dismissAssetSuggestion } = await import('../lib/api');
              await dismissAssetSuggestion(id);
              const u = await getReceipt(id);
              setData(u);
              return;
            } catch { /* fall through to legacy path */ }
          }
          await applyPatch({ potential_asset: false, asset_dismissed: true });
        }}
      />

      {/* Phase 25.3 — Transaction details (personal flag + payment method). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <IsPersonalToggle
          receipt={data}
          onChanged={async () => {
            try {
              const u = await (await import('../lib/api')).getReceipt(id);
              setData(u);
            } catch { /* noop */ }
          }}
        />
        <PaymentMethodPicker
          receipt={data}
          onChanged={async () => {
            try {
              const u = await (await import('../lib/api')).getReceipt(id);
              setData(u);
            } catch { /* noop */ }
          }}
        />
      </div>

      {/* Phase 25.2 — QuickBooks publishing (primary post-approval action). */}
      <PublishToQbButton
        receipt={data}
        key={`${data.is_personal}-${data.payment_method}`}
        onPublished={async () => {
          try {
            const u = await (await import('../lib/api')).getReceipt(id);
            setData(u);
          } catch { /* noop */ }
        }}
        onToast={(m) => { try { window.dispatchEvent(new CustomEvent('clasifi:toast', { detail: m })); } catch { /* noop */ } }}
      />

      <ReceiptForm
        data={data}
        categories={categories}
        notes={notes} setNotes={setNotes} setNotesDirty={setNotesDirty} onSaveNotes={onSaveNotes}
        openEdit={openEdit}
        applyPatch={applyPatch}
        colors={colors}
        suggestionSlot={(
          <CategorySuggestionChip
            data={data}
            colors={colors}
            onAccept={(cat) => applyPatch({ category: cat })}
          />
        )}
      />

      <TaxCheckPanel
        analysis={data?.tax_analysis ? { ...data.tax_analysis, _catalog: taxCatalog } : null}
        taxCodeOverride={data?.tax_code_override || null}
        colors={colors}
        onChangeTaxCode={() => setTaxCodePickerOpen(true)}
      />

      <ReceiptImages images={images} colors={colors} />

      {/* Phase 10: Splits UI */}
      {data.has_splits && data.splits?.length ? (
        <div className="rounded-xl border p-4 mb-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }} data-testid="splits-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
              ✂️ Splits ({data.splits.length})
            </p>
            <div className="flex gap-2">
              <button data-testid="edit-splits-btn"
                onClick={() => setSplitEditorOpen(true)}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border"
                style={{ borderColor: colors.border, color: colors.text }}>Edit</button>
              <button data-testid="clear-splits-btn"
                onClick={async () => {
                  if (!window.confirm('Clear all splits from this receipt?')) return;
                  try { const u = await clearSplitsApi(id); setData(u); showToast('Splits cleared'); }
                  catch (err) { showToast('Clear failed'); console.error(err); }
                }}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ color: colors.error }}>Clear</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.splits.map((s, i) => (
              <div key={s.id || i} className="flex items-center justify-between text-sm" data-testid={`split-item-${i}`}>
                <span className="font-semibold" style={{ color: colors.text }}>{s.category}</span>
                <span className="tabular-nums" style={{ color: colors.textMuted }}>
                  ${(s.amount || 0).toFixed(2)}
                  {(s.tax_gst || s.tax_qst) ? (
                    <span className="ml-2 text-[11px]">GST ${(s.tax_gst || 0).toFixed(2)} · QST ${(s.tax_qst || 0).toFixed(2)}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <button data-testid="open-split-editor"
          onClick={() => setSplitEditorOpen(true)}
          className="w-full mb-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors hover:opacity-80"
          style={{ borderColor: colors.border, color: colors.text, backgroundColor: colors.surface }}>
          ✂️ Split by category
        </button>
      )}

      <ReceiptActions
        saving={saving}
        onStatus={onStatus}
        onDelete={onDelete}
        colors={colors}
      />

      {ruleSuggestion ? (
        <RuleSuggestionModal
          colors={colors}
          suggestion={ruleSuggestion}
          onClose={() => setRuleSuggestion(null)}
        />
      ) : null}

      {splitEditorOpen ? (
        <SplitEditorModal
          receipt={data}
          colors={colors}
          onClose={() => setSplitEditorOpen(false)}
          onSaved={async () => {
            setSplitEditorOpen(false);
            try {
              const u = await (await import('../lib/api')).getReceipt(id);
              setData(u);
              showToast('Splits saved');
            } catch (err) { console.error(err); }
          }}
        />
      ) : null}

      <TaxCodePicker
        open={taxCodePickerOpen}
        initial={data?.tax_code_override || null}
        titleSuffix={data ? `· ${data.vendor || ''}` : ''}
        onCancel={() => setTaxCodePickerOpen(false)}
        onClear={async () => {
          setTaxCodePickerOpen(false);
          try {
            const u = await patchReceiptTaxCode(id, {
              federal_code: null, provincial_code: null, no_tax_applicable: false,
            });
            setData(u);
            showToast('Tax override cleared');
          } catch (err) {
            console.error(err);
            showToast(err?.message || 'Failed to clear tax override');
          }
        }}
        onApply={async (tc) => {
          setTaxCodePickerOpen(false);
          try {
            const u = await patchReceiptTaxCode(id, tc);
            setData(u);
            showToast('Tax code applied');
          } catch (err) {
            console.error(err);
            showToast(err?.message || 'Failed to apply tax code');
          }
        }}
      />

      {/* Edit modal */}
      {editField && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setEditField(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-field-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: colors.text }}>
                Edit {editField.label}
              </h3>
              <button
                onClick={() => setEditField(null)}
                className="p-1 rounded-md transition-opacity hover:opacity-70"
                style={{ color: colors.textMuted }}
              >
                <X size={18} />
              </button>
            </div>
            <input
              data-testid="edit-field-input"
              autoFocus
              type={editField.kind === 'decimal' ? 'number' : 'text'}
              step={editField.kind === 'decimal' ? '0.01' : undefined}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSaveEdit()}
              className="w-full px-4 py-3 rounded-xl border text-sm outline-none focus:ring-2"
              style={{
                backgroundColor: colors.bg,
                borderColor: colors.border,
                color: colors.text,
              }}
              placeholder={editField.kind === 'date' ? 'YYYY-MM-DD' : editField.kind === 'decimal' ? '0.00' : ''}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setEditField(null)}
                className="flex-1 h-10 rounded-full border text-sm font-medium"
                style={{ borderColor: colors.border, color: colors.textMuted }}
              >
                Cancel
              </button>
              <button
                data-testid="edit-field-save"
                onClick={onSaveEdit}
                className="flex-1 h-10 rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: colors.accent }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full text-sm font-semibold text-white z-50 shadow-lg"
          style={{ backgroundColor: colors.text }}
          data-testid="detail-toast"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
